// Site-wide restaurant ranking — the single source of truth for the order shown
// on the homepage table AND the "overall rank" badge on each restaurant page, so
// the two can never disagree. Order: most-picked first, ties broken by the
// better (lower) average rank.

import type { AggregatedUserPick } from '../types';

export function compareAggregated(a: AggregatedUserPick, b: AggregatedUserPick): number {
  if (b.userPickCount !== a.userPickCount) {
    return b.userPickCount - a.userPickCount;
  }
  return a.userAvgRank - b.userAvgRank;
}

/** Full ranking, highest first. Returns a sorted copy (does not mutate input). */
export function rankedAggregatedPicks(picks: AggregatedUserPick[]): AggregatedUserPick[] {
  return [...picks].sort(compareAggregated);
}

export interface OverallRank {
  rank: number; // 1-based position in the site-wide ranking
  total: number; // total ranked restaurants
}

/** The 1-based site-wide position of a place, or null if it isn't ranked yet. */
export function overallRankOf(
  picks: AggregatedUserPick[],
  placeId: string
): OverallRank | null {
  const sorted = rankedAggregatedPicks(picks);
  const index = sorted.findIndex((p) => p.placeId === placeId);
  if (index === -1) return null;
  return { rank: index + 1, total: sorted.length };
}
